import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Rating, createTheme, ThemeProvider } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

// Create a dark theme using Material-UI's theme creator
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#1E90FF", // Light blue primary color
    },
    background: {
      default: "#000000", // Dark background color
      paper: "#000000", // Slightly lighter background for paper elements
    },
    text: {
      primary: "#ffffff", // Light text color
      secondary: "#b0b0b0", // Lighter text color for secondary text
    },
    error: {
      main: "#f44336", // Red for error messages
    },
  },
});

const FeedbackFormRequestToUser = ({ userId }) => {
  const { id } = useParams();

  const [feedback, setFeedback] = useState({
    rating: 0,
    comments: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false); // Track if feedback was already submitted
  const [validationStatus, setValidationStatus] = useState(null);




  useEffect(() => {
    // Check initial validation status when component loads
    checkValidationStatus();
  }, []);

  const checkValidationStatus = async () => {
    try {
      // Send the post_id and user_id in the request body
      const response = await axios.post(`${API_URL}/api/booking-status-request-user/`, {
        session_id: id,
        // user_id: userId
      });
  
      // If the response contains validation status
      if (response.data.validation === 'success') {
        setValidationStatus('success');
        
      }
    } catch (error) {
      console.log('Error fetching validation status', error);
    
    }
  };

  useEffect(() => {
    // Check if feedback already exists for the given student and request
    const checkExistingFeedback = async () => {
      try {
        const response = await axios.post(`${API_URL}/check_feedback_request_to_user/`, {
          student_id: userId,
          id: id,
        });

        if (response.data.detail === "Feedback has already been submitted for this session.") {
          setAlreadySubmitted(true);
        }
      } catch (err) {
        console.error("Error checking existing feedback:", err);
      }
    };

    checkExistingFeedback();
  }, [userId, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({
      ...feedback,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (feedback.rating === 0) {
      setError("Please rate the session.");
      setLoading(false);
      return;
    }

    if (feedback.comments.trim() === "") {
      setError("Please provide a comment.");
      setLoading(false);
      return;
    }

    const data = {
      student_id: userId,
      id: id,
      student_rating: feedback.rating,
      student_comment: feedback.comments,
    };

    try {
      const response = await axios.post(`${API_URL}/create_user_feedback_request_to_user/`, data);
      if (response.status === 200) {
        setSubmitted(true);
      } else {
        setError("Something went wrong, please try again later.");
      }
    } catch (err) {
      setError("Failed to submit feedback. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            maxWidth: 500,
            margin: "0 auto",
            padding: "20px",
            marginTop: '100px',
            borderRadius: "8px",
            boxShadow: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="text.primary" sx={{letterSpacing: 4}}>
            Session Feedback
          </Typography>

          {alreadySubmitted ? (
            <Typography variant="h6" color="primary" sx={{letterSpacing: 3}}>
              You have already submitted feedback for this session.
            </Typography>
          ) : submitted ? (
            <Typography variant="h6" color="primary" sx={{letterSpacing: 3}}>
              Thank you for your feedback!
            </Typography>
          ) : (
            <form onSubmit={handleSubmit}>
              <Typography variant="body1" sx={{ marginBottom: "0.5rem", letterSpacing: 3 }} color="text.primary">
                How would you rate the session?
              </Typography>
              <Rating
                name="rating"
                value={feedback.rating}
                onChange={(_, newValue) => handleChange({ target: { name: "rating", value: newValue } })}
                precision={0.5}
                sx={{ marginBottom: "1rem", letterSpacing: 3 }}
              />

<TextField
  label="Comments"
  name="comments"
  value={feedback.comments}
  onChange={handleChange}
  fullWidth
  multiline
  rows={4}
  inputProps={{ maxLength: 100 }}
  helperText={`${feedback.comments.length}/100 characters`}
  FormHelperTextProps={{
    sx: {
      color: feedback.comments.length > 90 ? 'orange' : 'white',
      letterSpacing: 2,
    },
  }}
  sx={{ marginBottom: "1rem", letterSpacing: 3 }}
  InputLabelProps={{ style: { color: "white", letterSpacing: 3 } }}
  InputProps={{ style: { color: "white", letterSpacing: 3 } }}
/>


              {error && (
                <Typography variant="body2" color="error" sx={{ marginBottom: "1rem", letterSpacing: 3 }}>
                  {error}
                </Typography>
              )}

              <Button variant="contained" color="primary" type="submit" fullWidth 
              // disabled={loading} 
              disabled={loading || validationStatus !== 'success'}
              sx={{letterSpacing: 4}}>
                {loading ? "Submitting..." : "Submit Feedback"}
              </Button>
            </form>
          )}
        </Box>
      </ThemeProvider>
    </div>
  );
};

export default FeedbackFormRequestToUser;
